/**
 * Onboarding API Functions
 *
 * Onboarding state is stored in localStorage per user. This is intentional -
 * onboarding teaches the user how to use the app, so once learned it applies
 * across all workspaces. The app has fallback logic (checking if user has datasets)
 * to handle cases where localStorage is cleared.
 */

export const ONBOARDING_STORAGE_KEY = 'scoop_onboarding_state'

// Users with accounts older than this are considered existing users who don't need onboarding
export const ONBOARDING_GRACE_PERIOD_DAYS = 2

/**
 * Get the default onboarding state
 */
const getDefaultState = () => ({
    steps: {
        askQuestion: { completed: false, completedAt: null },
        openChart: { completed: false, completedAt: null },
        saveToCanvas: { completed: false, completedAt: null },
    },
    completedAt: null,
    dismissed: false,
})

/**
 * Get onboarding state from localStorage
 * @param {string} userID - User ID
 */
export const getOnboardingState = async (userID) => {
    const key = `${ONBOARDING_STORAGE_KEY}_${userID}`
    const stored = localStorage.getItem(key)

    if (stored) {
        try {
            return JSON.parse(stored)
        } catch {
            return getDefaultState()
        }
    }

    return getDefaultState()
}

/**
 * Update onboarding state in localStorage
 * @param {string} userID - User ID
 * @param {Object} state - New state to save
 */
export const updateOnboardingState = async (userID, state) => {
    const key = `${ONBOARDING_STORAGE_KEY}_${userID}`
    localStorage.setItem(key, JSON.stringify(state))

    return state
}

/**
 * Complete a specific onboarding step
 * @param {string} userID - User ID
 * @param {string} stepKey - Step key to complete
 * @param {Object} metadata - Optional metadata (e.g., { datasetId, prompt })
 */
export const completeOnboardingStep = async (userID, stepKey, metadata = {}) => {
    const currentState = await getOnboardingState(userID)
    const newState = {
        ...currentState,
        steps: {
            ...currentState.steps,
            [stepKey]: {
                completed: true,
                completedAt: new Date().toISOString(),
                ...metadata,
            },
        },
    }

    // Check if all steps are complete
    const allComplete =
        newState.steps.askQuestion?.completed &&
        newState.steps.openChart?.completed &&
        newState.steps.saveToCanvas?.completed

    if (allComplete && !newState.completedAt) {
        newState.completedAt = new Date().toISOString()
    }

    return await updateOnboardingState(userID, newState)
}

/**
 * Dismiss onboarding checklist (hide but preserve progress)
 * @param {string} userID - User ID
 */
export const dismissOnboarding = async (userID) => {
    const currentState = await getOnboardingState(userID)
    const newState = {
        ...currentState,
        dismissed: true,
    }
    return await updateOnboardingState(userID, newState)
}

/**
 * Reset onboarding state (for testing)
 * @param {string} userID - User ID
 */
export const resetOnboardingState = async (userID) => {
    const key = `${ONBOARDING_STORAGE_KEY}_${userID}`
    localStorage.removeItem(key)
    return getDefaultState()
}
